import type { Permission } from '@wr/shared';
import type { IconName } from './components/Icon.js';

/**
 * The portal's navigation (brief section 5, docs/prototype.html): the same
 * entries in the rail, the side menu, the phone bar and the home tiles,
 * each shown only to the people allowed to use it (section 9). An entry
 * whose screen is not built yet opens a page that says which build step
 * brings it (`step`).
 */
export type NavKey =
  | 'events'
  | 'todo'
  | 'travel'
  | 'people'
  | 'addContact'
  | 'addLead'
  | 'scan'
  | 'roi'
  | 'content'
  | 'packages'
  | 'expenses'
  | 'receipt'
  | 'kiosk'
  | 'recap'
  | 'settings'
  | 'wrapup';

export interface NavItem {
  key: NavKey;
  label: string;
  /** The side menu's longer name, when it differs. */
  menuLabel?: string;
  icon: IconName;
  path: string;
  allowed(can: (p: Permission) => boolean): boolean;
  /** The build step (brief section 13) that brings the screen; absent when built. */
  step?: number;
  /** Other pages that belong to the entry (it shows as current there). */
  also?: string[];
}

export const NAV: Record<NavKey, NavItem> = {
  events: {
    key: 'events',
    label: 'Events',
    icon: 'calendar',
    path: '/events',
    allowed: (can) => can('events.read'),
    step: 6,
  },
  todo: {
    key: 'todo',
    label: 'To do',
    icon: 'edit',
    path: '/todo',
    allowed: (can) => can('todo.use'),
    step: 3,
  },
  travel: {
    key: 'travel',
    label: 'Travel',
    icon: 'plane',
    path: '/travel',
    allowed: (can) => can('travel.read'),
    step: 9,
  },
  people: {
    key: 'people',
    label: 'Contacts/Leads',
    icon: 'contacts',
    path: '/people',
    allowed: (can) => can('contacts.read.own') || can('contacts.read.all'),
    step: 2,
  },
  addContact: {
    key: 'addContact',
    label: 'Add contact',
    icon: 'personadd',
    path: '/contacts/new',
    allowed: (can) => can('contacts.create'),
  },
  addLead: {
    key: 'addLead',
    label: 'Add lead',
    icon: 'leads',
    path: '/leads/new',
    allowed: (can) => can('leads.create'),
    step: 3,
  },
  scan: {
    key: 'scan',
    label: 'Scan',
    menuLabel: 'Scan a card',
    icon: 'scan',
    path: '/scan',
    allowed: (can) => can('contacts.scan'),
    step: 2,
  },
  roi: {
    key: 'roi',
    label: 'ROI',
    menuLabel: 'ROI simulator',
    icon: 'roi',
    path: '/roi',
    allowed: (can) => can('roi.use'),
    step: 7,
  },
  content: {
    key: 'content',
    label: 'Content',
    icon: 'content',
    path: '/content',
    allowed: (can) => can('content.read'),
    step: 6,
  },
  packages: {
    key: 'packages',
    label: 'Packages',
    icon: 'packages',
    path: '/packages',
    allowed: (can) =>
      can('packages.create') || can('packages.read.own') || can('packages.read.all'),
    step: 6,
  },
  expenses: {
    key: 'expenses',
    label: 'Expenses',
    icon: 'receipt',
    path: '/expenses',
    allowed: (can) => can('expenses.create'),
    step: 8,
  },
  receipt: {
    key: 'receipt',
    label: 'Receipt',
    icon: 'receipt',
    path: '/expenses/new',
    allowed: (can) => can('expenses.create'),
    step: 8,
  },
  kiosk: {
    key: 'kiosk',
    label: 'Booth kiosk',
    icon: 'content',
    path: '/kiosk',
    allowed: (can) => can('kiosk.start'),
    step: 6,
  },
  recap: {
    key: 'recap',
    label: 'Event recap',
    icon: 'roi',
    path: '/recap',
    allowed: (can) => can('recap.view'),
    step: 6,
  },
  // Administration, for admins only: not a configuration screen (brief section 12).
  settings: {
    key: 'settings',
    label: 'Settings',
    icon: 'settings',
    path: '/settings',
    also: ['/admin'],
    allowed: (can) => can('admin.users.read') || can('admin.audit.read'),
  },
  // Wrap up the current event, for admins (brief section 5, side menu: Event).
  wrapup: {
    key: 'wrapup',
    label: 'Wrap up',
    icon: 'packages',
    path: '/wrap-up',
    allowed: (can) => can('events.wrapup'),
    step: 6,
  },
};

/** Rail (tablet landscape and desktop), top to bottom, as the prototype. */
export const RAIL: NavKey[] = [
  'events',
  'todo',
  'travel',
  'people',
  'addContact',
  'addLead',
  'scan',
  'roi',
  'content',
  'packages',
  'expenses',
  'settings',
];

/** Phone bar: quick actions only (brief section 5). */
export const TABBAR: NavKey[] = ['addContact', 'scan', 'addLead', 'receipt'];

/** Side menu groups (phone and tablet portrait). */
export const MENU: { title: string; items: NavKey[] }[] = [
  { title: 'General', items: ['todo', 'events'] },
  { title: 'Capture', items: ['scan', 'addContact', 'addLead', 'people'] },
  { title: 'Marketing', items: ['content', 'roi', 'packages'] },
  { title: 'Event', items: ['travel', 'kiosk', 'recap', 'wrapup'] },
  { title: 'Me', items: ['expenses', 'settings'] },
];

/** Office day home tiles (no current event). */
export const HOME_TILES: NavKey[] = [
  'scan',
  'addContact',
  'addLead',
  'receipt',
  'roi',
  'packages',
  'content',
];

/** The screens opened as a window over the current page (prototype dialogs). */
export const DIALOG_PATHS = ['/contacts/new', '/leads/new'];

export function isCurrent(item: NavItem, pathname: string): boolean {
  return [item.path, ...(item.also ?? [])].some(
    (path) => pathname === path || pathname.startsWith(`${path}/`),
  );
}
